import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileSearch, TrendingDown } from 'lucide-react';
import { useStore } from 'context/StoreContext';
import { useApi } from 'hooks/useApi';
import { ProductService } from 'services/catalog/product.service';
import { SupplierService } from 'services/catalog/supplier.service';
import { Card } from 'components/common/Card';
import { EstimatedAmount } from 'components/common/EstimatedAmount';
import { EmptyState, LoadingScreen } from 'components/common/Spinner';
import { Badge } from 'components/common/Badge';
import { Button } from 'components/common/Button';
import { Dialog } from 'components/common/Dialog';
import { formatCurrency, formatPercent, formatDate } from 'utils/formatters';

const TABS = ['Overview', 'Cost History', 'Price History'];

export default function ProductDetailPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { org, currentStore } = useStore();
  const [tab, setTab] = useState(0);
  const [supplierId, setSupplierId] = useState('');
  const [supplierCost, setSupplierCost] = useState('');
  const [supplierSku, setSupplierSku] = useState('');
  const [supplierSaving, setSupplierSaving] = useState(false);
  const [costDialogOpen, setCostDialogOpen] = useState(false);
  const [costValue, setCostValue] = useState('');
  const [costDate, setCostDate] = useState(new Date().toISOString().slice(0, 10));
  const [costReason, setCostReason] = useState('');
  const [costSaving, setCostSaving] = useState(false);
  const [costError, setCostError] = useState<string | null>(null);

  const { data: detail, loading: detailLoading, refetch: refetchDetail } = useApi(
    () => ProductService.getDetail(org?.id ?? '', currentStore?.id ?? '', productId ?? ''),
    [org?.id, currentStore?.id, productId]
  );

  const { data: costHistory, refetch: refetchCostHistory } = useApi(
    () => ProductService.getCostHistory(org?.id ?? '', currentStore?.id ?? '', productId ?? ''),
    [org?.id, currentStore?.id, productId]
  );
  const { data: priceHistory } = useApi(
    () => ProductService.getPriceHistory(org?.id ?? '', currentStore?.id ?? '', productId ?? ''),
    [org?.id, currentStore?.id, productId]
  );
  const { data: suppliers } = useApi(
    () => SupplierService.list(org?.id ?? '', 1, 1000).then(response => response.data),
    [org?.id]
  );

  if (detailLoading && !detail) return <LoadingScreen />;
  if (!detail) return <EmptyState title="Product not found" message="It may have been removed, or it isn't sold at this store." />;

  const sp = detail;
  const product = detail;

  const hasCost = sp.current_cost !== null && sp.current_cost !== undefined;
  const margin = hasCost && sp.current_retail_price > 0
    ? (sp.gross_margin ?? ((sp.current_retail_price - sp.current_cost) / sp.current_retail_price))
    : null;
  // Store-level target, then the organization default. Values like 30 mean 30%.
  const rawTarget = Number((sp as any).target_margin ?? sp.target_margin_override ?? org?.target_margin ?? 0.30);
  const targetMargin = rawTarget > 1 ? rawTarget / 100 : rawTarget;
  const belowTarget = margin !== null && margin < targetMargin;
  const prevCost = costHistory?.[0]?.old_cost;
  const estimatedImpact = prevCost && hasCost
    ? Math.round((sp.current_cost - prevCost) * (sp.units_last_30d ?? 0))
    : undefined;
  // Price that reaches the target margin, rounded up to a .x9 ending.
  const targetPrice = hasCost && targetMargin < 1 ? Math.ceil((sp.current_cost / (1 - targetMargin)) * 10 - 0.9) / 10 + 0.09 : null;

  const openCostDialog = () => {
    setCostValue(sp.current_cost === null || sp.current_cost === undefined ? '' : String(sp.current_cost));
    setCostDate(new Date().toISOString().slice(0, 10));
    setCostReason('');
    setCostError(null);
    setCostDialogOpen(true);
  };

  const saveCost = async (event: React.FormEvent) => {
    event.preventDefault();
    setCostError(null);
    const value = Number(costValue);
    if (!Number.isFinite(value) || value < 0) {
      setCostError('Enter a non-negative cost.');
      return;
    }
    if (!costReason.trim()) {
      setCostError('Enter a reason for this cost change.');
      return;
    }
    setCostSaving(true);
    try {
      await ProductService.addManualCost(org?.id ?? '', currentStore?.id ?? '', productId ?? '', { unit_cost: value, effective_date: costDate, reason: costReason.trim() });
      setCostDialogOpen(false);
      refetchDetail();
      refetchCostHistory();
    } catch (error: any) {
      setCostError(error?.error ?? error?.message ?? 'Unable to save cost.');
    } finally {
      setCostSaving(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* Back */}
      <Button variant="ghost" size="sm" onClick={() => navigate('/products')}>
        <ArrowLeft size={14} /> Products
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{product.product_name}</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            UPC: {product.upc ?? '—'} · {product.brand} · {product.category}
          </p>
        </div>
        <Badge variant={product.active ? 'success' : 'default'}>{product.active ? 'Active' : 'Inactive'}</Badge>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Units/30d', value: (sp.units_last_30d ?? 0).toLocaleString() },
          { label: 'Retail Price', value: formatCurrency(sp.current_retail_price, 2) },
          { label: 'Current Cost', value: hasCost ? formatCurrency(sp.current_cost, 2) : '—' },
          {
            label: 'Gross Margin',
            value: margin === null ? '—' : formatPercent(margin),
            sub: `Target: ${formatPercent(targetMargin)}`,
            alert: belowTarget,
          },
        ].map(m => (
          <Card key={m.label}>
            <p className="text-xs text-gray-500">{m.label}</p>
            <p className={`text-xl font-bold mt-1 ${m.alert ? 'text-orange-600' : 'text-gray-900'}`}>{m.value}</p>
            {m.sub && (
              <p className={`text-xs mt-0.5 ${m.alert ? 'text-orange-500 flex items-center gap-1' : 'text-gray-400'}`}>
                {m.alert && <TrendingDown size={11} />}{m.sub}
              </p>
            )}
          </Card>
        ))}
      </div>

      {/* Estimated impact */}
      {estimatedImpact !== undefined && estimatedImpact > 0 && (
        <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg flex items-center gap-3">
          <TrendingDown size={18} className="text-orange-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-orange-800">Estimated margin impact from cost increase</p>
            <EstimatedAmount amount={estimatedImpact} className="text-sm mt-0.5" />
          </div>
        </div>
      )}

      {/* Tabs */}
      <Card padding={false}>
        <div className="flex border-b border-gray-200">
          {TABS.map((t, i) => (
            <button key={t} onClick={() => setTab(i)}
              className={`px-5 py-3 text-sm font-medium border-b-2 -mb-px ${tab === i ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t}
            </button>
          ))}
        </div>

        <div className="p-5">
          {tab === 0 && (
            <div className="space-y-3">
              <div className="border-b border-gray-100 pb-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900">Supplier costs</h3>
                  <span className="text-xs text-gray-400">Preferred cost drives margin</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="text-left text-xs text-gray-500"><th className="py-2">Supplier</th><th>Supplier SKU</th><th>Cost</th><th>Status</th><th /></tr></thead>
                    <tbody>
                      {(detail.suppliers ?? []).map((supplier: any) => (
                        <tr key={supplier.id} className="border-t border-gray-100">
                          <td className="py-2 font-medium">{supplier.supplier_name}</td>
                          <td>{supplier.supplier_sku ?? '—'}</td>
                          <td>{supplier.current_cost === null || supplier.current_cost === undefined ? '—' : formatCurrency(supplier.current_cost, 2)}</td>
                          <td>{supplier.preferred ? <Badge variant="success">Preferred</Badge> : '—'}</td>
                          <td className="text-right whitespace-nowrap">
                            {!supplier.preferred && <Button size="sm" variant="ghost" onClick={async () => { await ProductService.upsertSupplier(org?.id ?? '', currentStore?.id ?? '', productId ?? '', supplier.supplier_id, { current_cost: supplier.current_cost, supplier_sku: supplier.supplier_sku, preferred: true }); refetchDetail(); }}>Prefer</Button>}
                            <Button size="sm" variant="ghost" onClick={async () => { await ProductService.removeSupplier(org?.id ?? '', currentStore?.id ?? '', productId ?? '', supplier.supplier_id); refetchDetail(); }}>Remove</Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <form className="flex flex-wrap items-end gap-2 mt-3" onSubmit={async event => {
                  event.preventDefault();
                  if (!supplierId || supplierCost === '') return;
                  setSupplierSaving(true);
                  try {
                    await ProductService.upsertSupplier(org?.id ?? '', currentStore?.id ?? '', productId ?? '', supplierId, { current_cost: Number(supplierCost), supplier_sku: supplierSku || undefined, preferred: !(detail.suppliers ?? []).some((item: any) => item.preferred) });
                    setSupplierId(''); setSupplierCost(''); setSupplierSku('');
                    refetchDetail();
                  } finally { setSupplierSaving(false); }
                }}>
                  <select value={supplierId} onChange={event => setSupplierId(event.target.value)} className="border border-gray-300 rounded px-2 py-1.5 text-sm">
                    <option value="">Add supplier</option>
                    {(suppliers ?? []).filter(supplier => !(detail.suppliers ?? []).some((item: any) => item.supplier_id === supplier.id)).map(supplier => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}
                  </select>
                  <input value={supplierCost} onChange={event => setSupplierCost(event.target.value)} type="number" min="0" step="0.0001" placeholder="Cost" className="w-24 border border-gray-300 rounded px-2 py-1.5 text-sm" />
                  <input value={supplierSku} onChange={event => setSupplierSku(event.target.value)} placeholder="Supplier SKU" className="w-32 border border-gray-300 rounded px-2 py-1.5 text-sm" />
                  <Button size="sm" type="submit" loading={supplierSaving} disabled={!supplierId || supplierCost === ''}>Add supplier</Button>
                </form>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div><span className="text-gray-500">Inventory type:</span><span className="ml-2 font-medium">{sp.inventory_management_type.replace('_', ' ')}</span></div>
                <div><span className="text-gray-500">Case qty:</span><span className="ml-2 font-medium">{product.case_quantity ?? '—'}</span></div>
                <div><span className="text-gray-500">Taxable:</span><span className="ml-2 font-medium">{product.taxable ? 'Yes' : 'No'}</span></div>
              </div>
              <Button size="sm" variant="secondary" onClick={openCostDialog}>Edit Cost</Button>
              {belowTarget && targetPrice !== null && (
                <p className="mt-2 text-sm text-gray-700">To reach your {formatPercent(targetMargin)} target margin, price this item at <strong>{formatCurrency(targetPrice, 2)}</strong>.</p>
              )}
            </div>
          )}

          {tab === 1 && (
            <div className="space-y-2">
              {!costHistory?.length ? (
                <p className="text-sm text-gray-400">No cost history available.</p>
              ) : costHistory.map(entry => (
                <div key={entry.id} className="flex items-center gap-4 py-2 border-b border-gray-100 last:border-0">
                  <div className="text-sm">
                    <span className="font-semibold text-gray-900">{formatCurrency(entry.new_cost, 2)}</span>
                  </div>
                  <span className="text-xs text-gray-400">{formatDate(entry.effective_date)}</span>
                  <Badge variant="default">{entry.source}</Badge>
                  {entry.override_reason && <span className="text-xs text-gray-500">{entry.override_reason}</span>}
                  {entry.previous_unit_cost !== undefined && entry.previous_unit_cost !== null && <span className="text-xs text-gray-500">from {formatCurrency(entry.previous_unit_cost, 2)}</span>}
                  {entry.source_document_id && <Button size="sm" variant="ghost" onClick={() => navigate(`/costs/review/${entry.source_document_id}`)}><FileSearch size={14} /> Receipt</Button>}
                </div>
              ))}
            </div>
          )}

          {tab === 2 && (
            <div className="space-y-2">
              {!priceHistory?.length ? (
                <p className="text-sm text-gray-400">No price history available.</p>
              ) : priceHistory.map(entry => (
                <div key={entry.id} className="flex items-center gap-4 py-2 border-b border-gray-100 last:border-0">
                  <div className="text-sm">
                    <span className="text-gray-500">{formatCurrency(entry.old_price ?? 0, 2)}</span>
                    <span className="mx-2 text-gray-400">→</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(entry.new_price, 2)}</span>
                    {entry.old_price === entry.new_price && (
                      <span className="ml-2 text-xs text-gray-400">(unchanged)</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400">{formatDate(entry.effective_date)}</span>
                  <Badge variant="default">{entry.source}</Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      <Dialog open={costDialogOpen} title="Update Product Cost" onClose={() => setCostDialogOpen(false)}>
        <form className="space-y-4" onSubmit={saveCost}>
          <div>
            <p className="text-xs text-gray-500">Product</p>
            <p className="text-sm font-medium text-gray-900">{product.product_name}</p>
          </div>
          <label className="block text-sm text-gray-700">
            Cost per unit
            <input value={costValue} onChange={event => setCostValue(event.target.value)} type="number" min="0" step="0.0001" required className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" />
          </label>
          <label className="block text-sm text-gray-700">
            Effective date
            <input value={costDate} onChange={event => setCostDate(event.target.value)} type="date" required className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" />
          </label>
          <label className="block text-sm text-gray-700">
            Reason for change
            <textarea value={costReason} onChange={event => setCostReason(event.target.value)} required rows={3} placeholder="For example: supplier invoice update" className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2" />
          </label>
          {costError && <p className="text-sm text-red-600">{costError}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setCostDialogOpen(false)}>Cancel</Button>
            <Button type="submit" loading={costSaving}>Save Cost</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}

